/**
 * Helpers for keeping the "Via MPX Partners" diagonal hatch fill on the Japan
 * map's covered prefectures across Leaflet `setStyle()` calls.
 *
 * Why this exists: in Leaflet 1.9.4 `SVG._updateStyle()` runs on EVERY
 * `setStyle()` and, whenever `options.fill` is truthy, unconditionally re-sets
 * the path element's `fill` to `options.fillColor || options.color`. A covered
 * region's merged options still carry the solid-blue `fillColor` fallback, so a
 * partial `setStyle({ fillOpacity, weight })` on hover clobbers the injected
 * `fill="url(#mpx-hatch)"` back to solid blue. We therefore re-apply the hatch
 * fill to the element after each hover `setStyle()`.
 *
 * Kept Leaflet-free (operates on the minimal get/setAttribute surface) so it is
 * unit-testable in vitest's `node` environment without a DOM or Leaflet.
 */

/** The element surface these helpers need — satisfied by SVGElement. */
export interface FillElement {
    getAttribute(name: string): string | null
    setAttribute(name: string, value: string): void
}

/** Reference set on a covered region's path element when the hatch is applied. */
export const MPX_HATCH_FILL = 'url(#mpx-hatch)'

/** True when a fill value points at the #mpx-hatch pattern (vs the solid fallback). */
export function isHatchFill(fill: string | null | undefined): boolean {
    return fill === MPX_HATCH_FILL
}

/** Read an element's current `fill` attribute, tolerating a missing element. */
export function readRegionFill(element: FillElement | null | undefined): string | null {
    return element ? element.getAttribute('fill') : null
}

/**
 * Re-apply the hatch fill that a `setStyle()` clobbered. `savedFill` is the
 * element's resting fill captured once (before any hover). Only restores when
 * that resting fill was the hatch pattern, so the solid-blue fallback case
 * (hatch injection failed) is left untouched.
 */
export function restoreRegionFill(
    element: FillElement | null | undefined,
    savedFill: string | null | undefined,
): void {
    if (!element || !isHatchFill(savedFill)) return
    element.setAttribute('fill', MPX_HATCH_FILL)
}
